import React, { useEffect, useState } from "react";
import styles from "./styles.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDeviceType } from "hooks";
import { theme } from "constants/theme";
import { Navbar } from "components/navbar/index";
import back from "assets/images/back-icon.png";
import greenBack from "assets/images/green-back-icon.png";
import { Modal } from "components/modal";
import { deleteBlog, fetchBlogs, updateBlog } from "services/blogService";
import { Blog } from "types/blog";
import { CustomHeadline } from "components/ui";
import imageBackground from "assets/images/mobile-banner.png";
import moment from "moment";
import { CustomButtonSection } from "components/custom-button-section";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import SafeImage from "components/sections/section-13/SafeImage";

function trimWithWordBoundary(str: string, max: number): string {
  if (str.length <= max) return str;
  const trimmed = str.slice(0, max);
  return trimmed.slice(0, trimmed.lastIndexOf(" ")) + "...";
}

interface AdminArticlePageProps {
  handleClose: (sectionId: string, sectionNumber: number) => void;
}

export const AdminArticle: React.FC<AdminArticlePageProps> = ({
  handleClose,
}) => {
  const location = useLocation();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get("status");
    if (statusFromUrl) {
      setStatus(statusFromUrl);
    }
  }, [location.search]);

  const [translate, setTranslate] = useState(0);
  const [activeSection, setActiveSection] = useState<number>(0);
  const { device } = useDeviceType();
  const isDesktop = device === "desktop";
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [filteredBlogs, setFilteredBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<Blog | any>();

  const [status, setStatus] = useState("publish");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // default page size

  const [dynamicDefaultMobileInner, setDynamicDefaultMobileInner] =
    useState("");

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/admin");
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateStatus = (newStatus: string) => {
    setStatus(newStatus);
    const params = new URLSearchParams(location.search);
    params.set("status", newStatus);
    navigate({ search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    fetchBlogs()
      .then((data: Blog[]) => {
        setBlogs(data);
        const defaultImageData = data?.find((blog) => {
          return blog.status === "default-image";
        });
        setDynamicDefaultMobileInner(
          defaultImageData?.mobileInnerImageUrl || ""
        );
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (status && blogs && blogs.length > 0) {
      setLoading(true);
      const newData = blogs.filter((blog) => {
        return blog.status === status;
      });
      setFilteredBlogs(newData);
      setLoading(false);
    }
  }, [status, blogs]);

  const navigateToTop = () => navigate("/blog");

  const handleEditArticle = (slug: string) => {
    navigate(`/edit-article?id=${slug}`);
  };

  const handleNewArticle = () => {
    navigate("/new-article");
  };

  const handleDeleteArticle = async () => {
    setDeleting(true);
    try {
      await deleteBlog(selectedArticle.id)
        .then((res) => {})
        .catch(() => {
          setDeleting(false);
          setOpenDeleteModal(false);
        });
      await fetchBlogs()
        .then((data: Blog[]) => {
          setBlogs(data);
          setDeleting(false);
          setOpenDeleteModal(false);
        })
        .catch(() => {
          setDeleting(false);
          setOpenDeleteModal(false);
        });
    } catch (err) {
      console.error(err);
      setDeleting(false);
      setOpenDeleteModal(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      if (!selectedArticle.id) {
        console.error("Missing blog ID");
        return;
      }
      await updateBlog(selectedArticle.id, {
        ...selectedArticle,
        status: "publish",
      })
        .then((res) => {})
        .catch((err) => {
          console.log(err);
          setPublishing(false);
          setOpenPublishModal(false);
        });
      await fetchBlogs()
        .then((data: Blog[]) => {
          setBlogs(data);
          setPublishing(false);
          setOpenPublishModal(false);
        })
        .catch(() => {
          setPublishing(false);
          setOpenPublishModal(false);
        });
    } catch (err) {
      console.error(err);
      setDeleting(false);
      setOpenDeleteModal(false);
    }
  };

  const totalPages = Math.ceil(filteredBlogs.length / pageSize);

  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  return (
    <div className={styles.adminArticle}>
      <Navbar
        activeSection={activeSection}
        navigateToTop={navigateToTop}
        theme="black"
        logoVariant="noLogoText"
        blur
      />
      <Modal setTranslate={setTranslate} setActiveSection={setActiveSection} />
      <div className={styles.container}>
        <img src={back} alt="img" className={styles.closeButton} />

        <img
          src={isHovered ? greenBack : back}
          alt="img"
          className={styles.closeButton}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => navigate("/blog")}
        />

        <div className={styles.content}>
          <div className={styles.pageTitleBar}>
            <CustomHeadline
              size={isDesktop ? 55 : 24}
              text="Blog Articles"
              color={theme.colors.neutral[20]}
              styles={{
                lineHeight: `${isDesktop ? 52 : 26}px`,
                paddingLeft: "10px",
              }}
            />
            <div style={{ display: "flex" }}>
              <CustomButtonSection
                text="Default Images"
                colorScheme="green"
                onClick={() =>
                  navigate(
                    `/edit-article?id=${
                      process.env.REACT_APP_BLOG_DEFAULT_IMAGE_TITLE ||
                      "mindscriber-default-images"
                    }`
                  )
                }
                style={{
                  color: theme.colors.neutral[20],

                  background: theme.colors.green[90],
                }}
              />
              <CustomButtonSection
                text="Create Article"
                colorScheme="green"
                onClick={() => handleNewArticle()}
                style={{
                  color: theme.colors.neutral[20],
                  marginLeft: "10px",
                  background: theme.colors.green[90],
                }}
              />
            </div>
          </div>
          <div className={styles.tabContainer}>
            <div
              className={`${styles.tab} ${
                status === "publish" ? styles.tabActive : ""
              }`}
              onClick={() => updateStatus("publish")}
            >
              Published
            </div>
            <div
              className={`${styles.tab} ${
                status === "draft" ? styles.tabActive : ""
              }`}
              onClick={() => updateStatus("draft")}
            >
              Drafts
            </div>
          </div>
          <div className={styles.gridTable}>
            {/* Headers */}
            <div className={styles.gridHeader}>Articles</div>
            <div className={styles.gridHeader}>Date</div>
            <div className={styles.gridHeader}>Action</div>
            {loading && (
              <>
                <div className={styles.skeletonLoader} />
                <div className={styles.skeletonLoader} />
                <div className={styles.skeletonLoader} />
              </>
            )}
            {/* Row 1 */}
            {!loading && paginatedBlogs && paginatedBlogs.length > 0 && (
              <>
                {paginatedBlogs.map((blog) => {
                  if (!blog) return null;
                  return (
                    <>
                      {" "}
                      <div className={styles.gridCell}>
                        <div
                          className={styles.titleWithImageCell}
                          onClick={() =>
                            navigate(`/article/preview-article-${blog.slug}`)
                          }
                        >
                          {/* <img
                            src={
                              blog.mobileInnerImageUrl ||
                              dynamicDefaultMobileInner ||
                              imageBackground
                            }
                            onError={(e) => {
                              e.currentTarget.src =
                                dynamicDefaultMobileInner || imageBackground;
                            }}
                            alt="React"
                            className={styles.cellImg}
                          /> */}

                          <SafeImage
                            src={
                              blog.mobileInnerImageUrl ||
                              dynamicDefaultMobileInner ||
                              imageBackground
                            }
                            fallback={
                              dynamicDefaultMobileInner || imageBackground
                            }
                            alt={blog.title}
                            className={styles.cellImg}
                          />

                          <p className={styles.articleTitle}>
                            {trimWithWordBoundary(blog.title, 60)}
                          </p>
                        </div>
                      </div>
                      <div className={styles.gridCell}>
                        {moment(blog.date).format("MMM DD, YYYY")}
                      </div>
                      <div className={styles.gridCell}>
                        {status !== "publish" && (
                          <CustomButtonSection
                            disabled={publishing}
                            text={
                              publishing
                                ? selectedArticle?.id === blog.id
                                  ? "..."
                                  : "Publish"
                                : "Publish"
                            }
                            colorScheme="green"
                            onClick={() => {
                              if (blog.id) {
                                setSelectedArticle(blog);
                                setOpenPublishModal(true);
                              }
                            }}
                            style={{
                              color: theme.colors.neutral[20],
                              width: "100px",
                              minWidth: "100px",
                            }}
                          />
                        )}
                        <CustomButtonSection
                          text="Edit"
                          colorScheme="green"
                          onClick={() =>
                            blog.id && handleEditArticle(blog.slug)
                          }
                          style={{
                            color: theme.colors.neutral[20],
                            marginLeft: "10px",
                          }}
                        />
                        <CustomButtonSection
                          disabled={deleting}
                          text={
                            deleting
                              ? selectedArticle?.id === blog.id
                                ? "..."
                                : "Delete"
                              : "Delete"
                          }
                          colorScheme="green"
                          onClick={() => {
                            if (blog.id) {
                              setOpenDeleteModal(true);
                              setSelectedArticle(blog);
                            }
                          }}
                          style={{
                            color: theme.colors.neutral[20],
                            marginLeft: "10px",
                            width: "100px",
                            minWidth: "100px",
                          }}
                        />
                      </div>
                    </>
                  );
                })}
              </>
            )}
          </div>
          {!loading && (
            <>
              {paginatedBlogs && paginatedBlogs.length === 0 ? (
                <div className={styles.noRecordFound}>
                  <div>
                    <p className={styles.articleTitle}>
                      There are no articles here yet.
                    </p>
                    <p className={styles.articleTitle}>
                      Start by adding your first one!
                    </p>
                  </div>
                </div>
              ) : (
                <div className={styles.paginationWrapper}>
                  <CustomButtonSection
                    text="<<"
                    colorScheme="green"
                    onClick={goToFirstPage}
                    disabled={currentPage === 1}
                    style={{
                      color: theme.colors.neutral[50],
                      borderRadius: "8px 0px 0px 8px",
                      height: "40px",
                      padding: "4px 10px 6px",
                    }}
                  />
                  <CustomButtonSection
                    text={"<"}
                    colorScheme="green"
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    style={{
                      color: theme.colors.neutral[50],
                      height: "40px",
                      padding: "4px 10px 6px",
                    }}
                  />

                  {[...Array(totalPages)].map((_, i) => (
                    <CustomButtonSection
                      key={i}
                      text={`${i + 1}`}
                      onClick={() => setCurrentPage(i + 1)}
                      className={currentPage === i + 1 ? styles.activePage : ""}
                      style={{
                        color: theme.colors.neutral[50],
                        height: "40px",
                        padding: "4px 10px 6px",
                      }}
                    />
                  ))}
                  <CustomButtonSection
                    text={">"}
                    colorScheme="green"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    style={{
                      color: theme.colors.neutral[50],
                      height: "40px",
                      padding: "4px 10px 6px",
                    }}
                  />
                  <CustomButtonSection
                    text={">>"}
                    colorScheme="green"
                    onClick={goToLastPage}
                    disabled={currentPage === totalPages}
                    style={{
                      color: theme.colors.neutral[50],
                      height: "40px",
                      padding: "4px 10px 6px",
                      borderRadius: "0px 8px 8px 0px",
                    }}
                  />

                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1); // reset page when size changes
                    }}
                    className={styles.customSelect}
                    style={{
                      color: theme.colors.neutral[50],
                      height: "40px",
                      padding: "4px 20px 6px",
                      borderRadius: "8px",
                    }}
                  >
                    {[5, 10, 20, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} / page
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {openDeleteModal && selectedArticle && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <h2
              className={styles.header}
            >{`Delete ${selectedArticle.title}`}</h2>
            <p className={styles.subheading}>
              Are you sure you want to delete this article?
              <br />
              This action can’t be undone.
            </p>
            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.deleteButton}`}
                onClick={handleDeleteArticle}
                disabled={deleting}
              >
                {deleting ? "..." : "Delete"}
              </button>
              <button
                className={`${styles.button} ${styles.cancelButton}`}
                onClick={() => setOpenDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {openPublishModal && selectedArticle && (
        <div
          className={styles.overlay}
          onClick={() => {
            if (!publishing) {
              setSelectedArticle(null);
              setOpenPublishModal(false);
            }
          }}
        >
          <div className={styles.modal}>
            <h2 className={styles.header}>{`Ready to publish?`}</h2>
            <p className={styles.subheading}>
              This draft may not be finished yet.
              <br />
              Do you still want to publish it?
            </p>
            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.deleteButton}`}
                onClick={handlePublish}
                disabled={publishing}
                style={{
                  background: theme.colors.green[90],
                  color: theme.colors.neutral[20],
                }}
              >
                {publishing ? "..." : "Publish"}
              </button>
              <button
                className={`${styles.button} ${styles.cancelButton}`}
                onClick={() => {
                  handleEditArticle(selectedArticle.slug);
                  setOpenDeleteModal(false);
                }}
                disabled={publishing}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
